/**
 *  @class
 *  @function Predictive Search
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('.thb-predictive-search');
      this.initialPredictiveSearchMarkup = this.predictiveSearchResults.innerHTML;
      this.cc = this.querySelector('.searchform--click-capture');
      this.mobileToggle = this.querySelector('.searchform--mobile-toggle');
      this.mobileClose = this.querySelector('.searchform--mobile-close');
      this.mobileMq = window.matchMedia('(max-width: 1067px)');

      this.setupEventListeners();

      this.onMobileMqChange = () => {
        if (!this.mobileMq.matches) {
          this.classList.remove('searchform--mobile-expanded');
          if (this.mobileToggle) {
            this.mobileToggle.setAttribute('aria-expanded', 'false');
          }
        }
        this.updateMobileInputTabindex();
      };
      this.mobileMq.addEventListener('change', this.onMobileMqChange);
      this.updateMobileInputTabindex();

      if (this.mobileToggle) {
        this.mobileToggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!this.mobileMq.matches) return;
          if (this.classList.contains('searchform--mobile-expanded')) {
            this.close();
            return;
          }
          this.classList.add('searchform--mobile-expanded');
          this.mobileToggle.setAttribute('aria-expanded', 'true');
          this.updateMobileInputTabindex();
          requestAnimationFrame(() => {
            this.input.focus({ preventScroll: true });
          });
        });
      }

      if (this.mobileClose) {
        this.mobileClose.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.close();
        });
      }

      // Close search when clicking outside the search form
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) {
          this.close();
        }
      });
    }

    updateMobileInputTabindex() {
      if (!this.input) return;
      const isMobile = this.mobileMq && this.mobileMq.matches;
      const expanded = this.classList.contains('searchform--mobile-expanded');
      if (isMobile && !expanded) {
        this.input.setAttribute('tabindex', '-1');
      } else {
        this.input.removeAttribute('tabindex');
      }
    }

    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.predictiveSearchResults.addEventListener('focusout', this.close.bind(this));

      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );

      this.cc.addEventListener('click', this.close.bind(this));

      document.addEventListener('keyup', (e) => {
        if (e.code && e.code.toUpperCase() === 'ESCAPE') {
          this.close();
        }
      });

      this.input.addEventListener('focus', this.open.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        document.body.classList.remove('open-search');
        this.predictiveSearchResults.setAttribute('inert', '');
        return;
      }

      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
      this.getSearchResults(searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    }

    onFocus() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) return;

      this.getSearchResults(searchTerm);
    }

    getSearchResults(searchTerm) {
      this.predictiveSearchResults.classList.add('loading');

      fetch(
        `${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,article,query,collection&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search`
      )
        .then((response) => {
          this.predictiveSearchResults.classList.remove('loading');
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search').innerHTML;

          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          throw error;
        });
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;

      const submitButton = this.querySelector('#search-results-submit');
      if (submitButton) {
        submitButton.addEventListener('click', () => {
          this.form.submit();
        });
      }
    }

    close() {
      if (this.input) {
        this.input.value = '';
      }
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.classList.remove('loading');
        this.predictiveSearchResults.innerHTML = this.initialPredictiveSearchMarkup;
      }
      this.predictiveSearchResults.setAttribute('inert', '');
      document.body.classList.remove('open-search');
      this.classList.remove('searchform--mobile-expanded');
      if (this.mobileToggle) {
        this.mobileToggle.setAttribute('aria-expanded', 'false');
      }
      this.updateMobileInputTabindex();
    }

    open() {
      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
    }
  }

  customElements.define('search-form', SearchForm);
}